import { API_URL as BASE_URL } from '../../lib/config';
const API_URL = `${BASE_URL}/platform-admin`;

export interface BatchOperationResult {
  success: boolean;
  totalProcessed: number;
  successful: number;
  failed: number;
  errors: Array<{ id: string; error: string }>;
  details?: any;
}

export interface BatchPasswordResetDto {
  userIds?: string[];
  organizationIds?: string[];
  emails?: string[];
  newPassword: string;
  sendEmail?: boolean;
}

export interface BatchUpdateOrganizationsDto {
  organizationIds: string[];
  updates: {
    status?: 'ACTIVE' | 'PAUSED' | 'TRIAL' | 'DELETED' | 'SUSPENDED';
    pausedReason?: string;
    billingEmail?: string;
  };
}

export interface BatchDeleteUsersDto {
  userIds: string[];
  reason?: string;
}

export interface BatchUpdateUsersDto {
  userIds: string[];
  updates: {
    role?: 'ADMIN' | 'MANAGER' | 'SUPERVISOR' | 'AGENT';
    isActive?: boolean;
  };
}

export interface BatchStatistics {
  totalUsers: number;
  totalOrganizations: number;
  activeOrganizations: number;
  pausedOrganizations: number;
}

export const batchOperationsService = {
  /**
   * Get batch operation statistics
   */
  async getStatistics(token: string): Promise<BatchStatistics> {
    const response = await fetch(`${API_URL}/batch/statistics`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch statistics');
    }

    return response.json();
  },

  /**
   * Batch password reset
   */
  async batchPasswordReset(
    token: string,
    data: BatchPasswordResetDto
  ): Promise<BatchOperationResult> {
    const response = await fetch(`${API_URL}/batch/password-reset`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reset passwords');
    }

    return response.json();
  },

  /**
   * Batch update organizations
   */
  async batchUpdateOrganizations(
    token: string,
    data: BatchUpdateOrganizationsDto
  ): Promise<BatchOperationResult> {
    const response = await fetch(`${API_URL}/batch/update-organizations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update organizations');
    }

    return response.json();
  },

  /**
   * Batch delete users
   */
  async batchDeleteUsers(
    token: string,
    data: BatchDeleteUsersDto
  ): Promise<BatchOperationResult> {
    const response = await fetch(`${API_URL}/batch/delete-users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete users');
    }

    return response.json();
  },

  /**
   * Batch update users
   */
  async batchUpdateUsers(
    token: string,
    data: BatchUpdateUsersDto
  ): Promise<BatchOperationResult> {
    const response = await fetch(`${API_URL}/batch/update-users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update users');
    }

    return response.json();
  },
};
